/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { useRef, useState } from 'react';

import classNames from 'classnames';

import { disableScrolling } from 'bundles/browse/utils';
import SimplifiedMegaMenuContainer from 'bundles/megamenu/components/SimplifiedMegaMenuContainer';
import ExploreButton from 'bundles/page-header/components/redesign-experiment/ExploreButton';

import _t from 'i18n!nls/page';

import 'css!bundles/page-header/components/desktop/mega-menu/__styles__/MegaMenuWrapper';

type MenuState = {
  menuIsOpen: boolean;
};

const styles = {
  simplifiedMegaMenuOverlay: css`
    background-color: inherit;
  `,
};

const SimplifiedMegaMenuWrapper = () => {
  const [menuState, setMenuState] = useState<MenuState>({
    menuIsOpen: false,
  });

  const divRef = useRef<HTMLDivElement | null>(null);
  const cursorIsOnMenuOrExploreButton = useRef<boolean | undefined>(undefined);
  const exploreButtonRef = useRef<HTMLButtonElement | null>(null);

  const setExploreButtonRef = (buttonRef: HTMLButtonElement | null) => {
    exploreButtonRef.current = buttonRef;
  };

  // close megamenu when component and button lose focus or esc key is pressed
  const handleFocusOut = (event: FocusEvent) => {
    if (divRef.current && !divRef.current.contains(event.relatedTarget as Node)) {
      closeMenu();
    }
  };
  const handleKeyUp = (event: KeyboardEvent) => {
    if (divRef.current && event.key === 'Escape') {
      closeMenu();
    }
  };

  const attachEventListeners = () => {
    document.addEventListener('focusout', handleFocusOut);
    document.addEventListener('keyup', handleKeyUp);
  };

  const detatchEventListeners = () => {
    document.removeEventListener('focusout', handleFocusOut);
    document.removeEventListener('keyup', handleKeyUp);
  };

  const setMenuOverlay = () => {
    if (cursorIsOnMenuOrExploreButton.current) {
      exploreButtonRef.current?.focus();
      attachEventListeners();
      disableScrolling(true);
      setMenuState({ menuIsOpen: true });
    }
  };

  const openMenu = () => {
    cursorIsOnMenuOrExploreButton.current = true;
    if (!menuState.menuIsOpen) {
      setTimeout(() => {
        setMenuOverlay();
      }, 200);
    }
  };

  const openMenuUsingKeyboard = () => {
    cursorIsOnMenuOrExploreButton.current = true;
    if (!menuState.menuIsOpen) {
      setMenuOverlay();
    }
  };

  const closeMenu = () => {
    cursorIsOnMenuOrExploreButton.current = false;
    setTimeout(() => {
      if (!cursorIsOnMenuOrExploreButton.current) {
        detatchEventListeners();
        disableScrolling(false);
        setMenuState({ menuIsOpen: false });
      }
    }, 100);
  };

  const { menuIsOpen } = menuState;

  const overlayClassNames = classNames('mega-menu-overlay', 'mega-menu-overlay--lazy-loading', {
    'is-active': menuIsOpen,
  });

  return (
    <div ref={divRef}>
      <ExploreButton
        menuIsOpen={menuIsOpen}
        openMenu={openMenu}
        openMenuUsingKeyboard={openMenuUsingKeyboard}
        closeMenu={closeMenu}
        setExploreButtonRef={setExploreButtonRef}
      />
      <div css={styles.simplifiedMegaMenuOverlay} className={overlayClassNames} aria-hidden={!menuIsOpen}>
        <nav onMouseEnter={openMenu} onMouseLeave={closeMenu} aria-label={_t('Main Menu')}>
          <SimplifiedMegaMenuContainer menuIsOpen={menuIsOpen} openMenu={openMenu} closeMenu={closeMenu} />
        </nav>
      </div>
    </div>
  );
};

export default SimplifiedMegaMenuWrapper;
